/**
 * Emergency care & pharmacy finder — shared types and helpers.
 * Real facility data comes from OpenStreetMap via the Overpass API
 * (see src/lib/overpass.ts).
 */

export type FacilityType = 'pharmacy' | 'hospital' | 'clinic';

export interface Facility {
  id: string;
  name: string;
  type: FacilityType;
  lat: number;
  lon: number;
  /** Distance from the user in km (computed client-side). */
  distanceKm: number;
  /** Normalized phone number for tel: links, when listed in OSM. */
  phone?: string;
  address?: string;
  /** Raw OSM opening_hours string, when available. */
  openingHours?: string;
  /** True when OSM marks the facility 24/7. */
  open24h: boolean;
  /** True when OSM marks emergency services (emergency=yes). */
  hasEmergency: boolean;
}

export interface Coords {
  lat: number;
  lon: number;
}

/** Fallback center (Nairobi CBD) when geolocation is unavailable. */
export const FALLBACK_CENTER: Coords = { lat: -1.2864, lon: 36.8172 };
export const FALLBACK_LABEL = 'Nairobi city centre';

/** Great-circle distance in km. */
export function haversineKm(a: Coords, b: Coords): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export type OpenStatus =
  | { state: 'open'; label: string }
  | { state: 'closed'; label: string }
  | { state: 'unknown'; label: string };

function pad(value: string): string {
  return value.padStart(2, '0');
}

/** Interpret an OSM opening_hours string well enough for a status line. */
function interpretOpeningHours(openingHours: string, now: Date): OpenStatus {
  const range = /(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})/.exec(openingHours);
  if (!range) {
    const trimmed = openingHours.length > 42 ? `${openingHours.slice(0, 42)}…` : openingHours;
    return { state: 'unknown', label: `Hours: ${trimmed}` };
  }

  const openMin = Number(range[1]) * 60 + Number(range[2]);
  const closeMin = Number(range[3]) * 60 + Number(range[4]);
  const currentMin = now.getHours() * 60 + now.getMinutes();

  // Overnight ranges (e.g. 18:00-06:00).
  const isOpen =
    closeMin <= openMin
      ? currentMin >= openMin || currentMin < closeMin
      : currentMin >= openMin && currentMin < closeMin;

  return isOpen
    ? { state: 'open', label: `Open now · Closes ${pad(range[3])}:${range[4]}` }
    : { state: 'closed', label: `Closed · Opens ${pad(range[1])}:${range[2]}` };
}

/** Operating status right now, from OSM opening-hours data when present. */
export function facilityStatus(facility: Facility, now: Date = new Date()): OpenStatus {
  if (facility.open24h) return { state: 'open', label: 'Open 24 hours' };
  if (facility.openingHours) return interpretOpeningHours(facility.openingHours, now);
  return { state: 'unknown', label: 'Hours not listed' };
}

/** Pretty distance: "350 m" under a kilometre, otherwise "2.4 km". */
export function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

/** Google Maps directions URL. */
export function directionsUrl(facility: Facility): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${facility.lat},${facility.lon}`;
}

/**
 * Google Maps place search URL — the place card usually shows the phone
 * number when OpenStreetMap doesn't list one.
 */
export function lookupUrl(facility: Facility): string {
  return `https://www.google.com/maps/search/${encodeURIComponent(facility.name)}/@${facility.lat},${facility.lon},18z`;
}
